import { DateOnly } from '@if/core/date'
import { EMerchantType } from '@if/core/types'
import {
  ControlledDateOnlyPicker,
  ControlledResourceAutocomplete,
  FieldLabel,
  FormContainer,
  useFormContainerContext,
} from '@if/mui'
import { Card, CardContent, Grid, Stack } from '@mui/material'
import { EARLIER_LUCAS_DATE } from '@src/types/Constants'
import { Merchant } from '@src/types/Merchant'
import { AggregationPeriod, MetricDisplayMode } from '@src/types/MerchantStatistic'
import { useEffect, useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { getQuickDatePresetRange, QuickDatePreset } from '../statistics-hooks'
import { createStatisticsFilterSchema, StatisticsFilterFormValues } from '../statistics-schema'
import { AggregationToggle } from './AggregationToggle'
import { QuickDatePresets } from './QuickDatePresets'
import { ViewModeToggle } from './ViewModeToggle'

interface StatisticsFiltersProps {
  currentMerchant: Merchant
  subsidiaryMerchants: Merchant[]
  defaultFromDate: string
  defaultToDate: string
  onSubmit: (merchantId: string, fromDate: string, toDate: string) => void
  aggregation: AggregationPeriod
  onAggregationChange: (aggregation: AggregationPeriod) => void
  displayMode: MetricDisplayMode
  onDisplayModeChange: (mode: MetricDisplayMode) => void
}

interface FilterFieldsProps {
  currentMerchant: Merchant
  availableMerchants: Merchant[]
  aggregation: AggregationPeriod
  onAggregationChange: (aggregation: AggregationPeriod) => void
  displayMode: MetricDisplayMode
  onDisplayModeChange: (mode: MetricDisplayMode) => void
}

const FilterFields = ({
  currentMerchant,
  availableMerchants,
  aggregation,
  onAggregationChange,
  displayMode,
  onDisplayModeChange,
}: FilterFieldsProps) => {
  const { t } = useTranslation()
  const { methods, submit } = useFormContainerContext<StatisticsFilterFormValues>()
  const { control, setValue } = methods

  const watchedFromDate = useWatch({ control, name: 'fromDate' })
  const watchedToDate = useWatch({ control, name: 'toDate' })

  const spanDays =
    watchedFromDate && watchedToDate
      ? Math.round((watchedToDate.getTime() - watchedFromDate.getTime()) / 86_400_000) + 1
      : 0
  const disableDailyAggregation = spanDays > 90

  useEffect(() => {
    if (disableDailyAggregation && aggregation === AggregationPeriod.Daily) {
      onAggregationChange(AggregationPeriod.Weekly)
    }
  }, [disableDailyAggregation, aggregation, onAggregationChange])

  const handlePresetSelect = (preset: QuickDatePreset) => {
    const range = getQuickDatePresetRange(preset)
    setValue('fromDate', new DateOnly(range.fromDate), { shouldValidate: true, shouldDirty: true })
    setValue('toDate', new DateOnly(range.toDate), { shouldValidate: true, shouldDirty: true })
    void submit('quickPreset')
  }

  return (
    <Stack spacing={2}>
      <ControlledResourceAutocomplete
        name="merchantId"
        control={control}
        label={t('merchantStatistics.merchantLoanActivity.selectMerchant')}
        disabled={availableMerchants.length <= 1}
        defaultOption={availableMerchants.find((merchant) => merchant.id === currentMerchant.id) ?? null}
        getOptionLabel={(merchant) => merchant.name}
        getOptionId={(merchant) => merchant.id}
        useSearch={(search) => ({
          data: search
            ? availableMerchants.filter((merchant) => merchant.name.toLowerCase().includes(search.toLowerCase()))
            : availableMerchants,
          isLoading: false,
          isFetching: false,
        })}
      />

      <Grid container spacing={2} alignItems="flex-end">
        <Grid size="grow">
          <ControlledDateOnlyPicker
            name="fromDate"
            control={control}
            label={t('merchantStatistics.merchantLoanActivity.fromDate')}
          />
        </Grid>
        <Grid size="grow">
          <ControlledDateOnlyPicker
            name="toDate"
            control={control}
            label={t('merchantStatistics.merchantLoanActivity.toDate')}
          />
        </Grid>
        <Grid size="auto">
          <QuickDatePresets
            fromDate={watchedFromDate?.toString() ?? ''}
            toDate={watchedToDate?.toString() ?? ''}
            onSelect={handlePresetSelect}
          />
        </Grid>
      </Grid>

      <Stack direction="row" spacing={3} flexWrap="wrap">
        <Stack spacing={0.5}>
          <FieldLabel>{t('merchantStatistics.performance.groupByLabel')}</FieldLabel>
          <AggregationToggle
            aggregation={aggregation}
            onAggregationChange={onAggregationChange}
            disableDaily={disableDailyAggregation}
          />
        </Stack>
        <Stack spacing={0.5}>
          <FieldLabel>{t('merchantStatistics.performance.viewLabel')}</FieldLabel>
          <ViewModeToggle value={displayMode} onChange={onDisplayModeChange} />
        </Stack>
      </Stack>
    </Stack>
  )
}

export const StatisticsFilters = ({
  currentMerchant,
  subsidiaryMerchants,
  defaultFromDate,
  defaultToDate,
  onSubmit,
  aggregation,
  onAggregationChange,
  displayMode,
  onDisplayModeChange,
}: StatisticsFiltersProps) => {
  const availableMerchants = useMemo(
    () =>
      currentMerchant.merchantTypeId === EMerchantType.Banner
        ? [currentMerchant, ...subsidiaryMerchants]
        : [currentMerchant],
    [currentMerchant, subsidiaryMerchants],
  )

  return (
    <Card>
      <CardContent>
        <FormContainer
          mode="filters"
          useFormSetup={() => ({
            schema: createStatisticsFilterSchema(new DateOnly(currentMerchant.firstOnboardedOn || EARLIER_LUCAS_DATE)),
            defaultValues: {
              merchantId: currentMerchant.id,
              fromDate: new DateOnly(defaultFromDate),
              toDate: new DateOnly(defaultToDate),
            },
          })}
          onSubmit={(values) => {
            onSubmit(values.merchantId, values.fromDate.toString(), values.toDate.toString())
          }}
        >
          <FilterFields
            currentMerchant={currentMerchant}
            availableMerchants={availableMerchants}
            aggregation={aggregation}
            onAggregationChange={onAggregationChange}
            displayMode={displayMode}
            onDisplayModeChange={onDisplayModeChange}
          />
        </FormContainer>
      </CardContent>
    </Card>
  )
}
